/** Delivery Reconciliation (Demo Mode only) - the operational-finance bridge between
 * Procurement, Goods Receipting, Invoices and Credits. Confirms that what was ordered,
 * delivered, receipted, invoiced and credited is consistent, and surfaces financial exceptions
 * that affect food cost or margin on Commercial Performance. No backend reconciliation feed
 * exists yet - all figures are static mock data (see
 * src/mocks/data/deliveryReconciliationData.ts), matching the approach used by
 * src/pages/FinanceDashboard and src/pages/EstateManagement. */

import { Fragment, useMemo, useState } from 'react';
import {
  Box, Chip, Collapse, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { Section } from '../../components/ui/Section';
import { TableHeader } from '../../components/forms/TableHeader';
import { Pill } from '../../components/shared/Pill';
import CustomPagination from '../../components/forms/Pagination';
import { applyRoundAndFormat } from '../../utils';
import useCurrencyCodeConfiguration from '../../hooks/configurations/useCurrencyCodeConfiguration';
import { DELIVERY_RECONCILIATION_ROWS, ReconciliationStatus } from '../../mocks/data/deliveryReconciliationData';

const STATUS_FILTERS: ReconciliationStatus[] = ['Matched', 'Awaiting goods receipt', 'Quantity variance', 'Price variance', 'Disputed'];

const STATUS_CHIP_COLOR: Record<ReconciliationStatus, 'success' | 'default' | 'warning' | 'error'> = {
  Matched: 'success',
  'Awaiting goods receipt': 'default',
  'Quantity variance': 'warning',
  'Price variance': 'warning',
  Disputed: 'error',
};

const DeliveryReconciliation = () => {
  const { t } = useTranslation('common');
  const { currencyCode } = useCurrencyCodeConfiguration();
  const [statusFilter, setStatusFilter] = useState<ReconciliationStatus | ''>('');
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [page, setPage] = useState(0);

  const filteredRows = useMemo(
    () => DELIVERY_RECONCILIATION_ROWS.filter((row) => !statusFilter || row.status === statusFilter),
    [statusFilter],
  );

  return (
    <PageArea>
      <PageTitle subTitle={t('deliveryReconciliationSubtitle', 'Was it ordered, delivered, receipted, invoiced and credited consistently?')}>
        {t('deliveryReconciliationTitle', 'Delivery Reconciliation')}
      </PageTitle>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 4, mt: 4, flexWrap: 'wrap' }}>
        {STATUS_FILTERS.map((status) => (
          <Chip key={status}
            label={t(`deliveryReconciliationStatus${status.replace(/\s/g, '')}`, status)}
            color={statusFilter === status ? STATUS_CHIP_COLOR[status] : 'default'}
            onClick={() => setStatusFilter((prev) => (prev === status ? '' : status))} />
        ))}
      </Box>

      <Section>
        <TableHeader>
          {t('deliveryReconciliationTableTitle', 'Order to invoice reconciliation')}
          <Pill text={`${filteredRows.length} ${t('deliveryReconciliationItemsLabel', 'items')}`} />
        </TableHeader>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>{t('deliveryReconciliationSite', 'Site')}</TableCell>
                <TableCell>{t('deliveryReconciliationSupplier', 'Supplier')}</TableCell>
                <TableCell>{t('deliveryReconciliationOrder', 'Order')}</TableCell>
                <TableCell>{t('deliveryReconciliationProduct', 'Product')}</TableCell>
                <TableCell align="right">{t('deliveryReconciliationOrderedQty', 'Ordered qty')}</TableCell>
                <TableCell align="right">{t('deliveryReconciliationReceivedQty', 'Received qty')}</TableCell>
                <TableCell align="right">{t('deliveryReconciliationInvoicedQty', 'Invoiced qty')}</TableCell>
                <TableCell align="right">{t('deliveryReconciliationVariance', 'Variance')}</TableCell>
                <TableCell>{t('deliveryReconciliationExceptionReason', 'Exception reason')}</TableCell>
                <TableCell>{t('deliveryReconciliationStatus', 'Status')}</TableCell>
                <TableCell>{t('deliveryReconciliationOwner', 'Owner')}</TableCell>
                <TableCell>{t('deliveryReconciliationDueDate', 'Due date')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.map((row) => {
                const isExpanded = expandedRow === row.id;
                return (
                  <Fragment key={row.id}>
                    <TableRow>
                      <TableCell>
                        <IconButton size="small"
                          onClick={() => setExpandedRow(isExpanded ? null : row.id)}>
                          {isExpanded ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                        </IconButton>
                      </TableCell>
                      <TableCell>{row.site}</TableCell>
                      <TableCell>{row.supplier}</TableCell>
                      <TableCell>{row.order}</TableCell>
                      <TableCell>{row.product}</TableCell>
                      <TableCell align="right">{row.orderedQuantity}</TableCell>
                      <TableCell align="right">{row.receivedQuantity}</TableCell>
                      <TableCell align="right">{row.invoicedQuantity}</TableCell>
                      <TableCell align="right">{`${row.variancePercentage > 0 ? '+' : ''}${row.variancePercentage.toFixed(1)}%`}</TableCell>
                      <TableCell>{row.exceptionReason ?? '-'}</TableCell>
                      <TableCell>
                        <Chip size="small"
                          label={t(`deliveryReconciliationStatus${row.status.replace(/\s/g, '')}`, row.status)}
                          color={STATUS_CHIP_COLOR[row.status]}
                          variant="outlined" />
                      </TableCell>
                      <TableCell>{row.owner}</TableCell>
                      <TableCell>{row.dueDate}</TableCell>
                    </TableRow>
                    <TableRow key={`${row.id}-detail`}>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }}
                        colSpan={12}>
                        <Collapse in={isExpanded}
                          timeout={250}
                          unmountOnExit>
                          <Box sx={{ py: 3 }}>
                            <Section style={{ boxShadow: 'none' }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>{t('deliveryReconciliationDelivery', 'Delivery')}</TableCell>
                                    <TableCell>{t('deliveryReconciliationGoodsReceipt', 'Goods receipt')}</TableCell>
                                    <TableCell>{t('deliveryReconciliationInvoice', 'Invoice')}</TableCell>
                                    <TableCell>{t('deliveryReconciliationCredit', 'Credit')}</TableCell>
                                    <TableCell align="right">{t('deliveryReconciliationUnitPrice', 'Unit price')}</TableCell>
                                    <TableCell>{t('deliveryReconciliationPackSize', 'Pack size')}</TableCell>
                                    <TableCell>{t('deliveryReconciliationUom', 'Unit of measure')}</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  <TableRow>
                                    <TableCell>{row.delivery}</TableCell>
                                    <TableCell>{row.goodsReceipt}</TableCell>
                                    <TableCell>{row.invoice}</TableCell>
                                    <TableCell>{row.credit ?? '-'}</TableCell>
                                    <TableCell align="right">{applyRoundAndFormat(row.unitPrice, currencyCode)}</TableCell>
                                    <TableCell>{row.packSize}</TableCell>
                                    <TableCell>{row.unitOfMeasure}</TableCell>
                                  </TableRow>
                                </TableBody>
                              </Table>
                            </Section>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                );
              })}
              {filteredRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={12}>
                    <Typography variant="textSM"
                      color="text.secondary">
                      {t('deliveryReconciliationNoResults', 'No reconciliation items match the selected filter.')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <CustomPagination page={page}
          setPage={setPage}
          maxPages={1}
          useNoDataMessage={false}
          breakpointForChangeDisplay={120000} />
      </Section>
    </PageArea>
  );
};

export default DeliveryReconciliation;
